import React, { useEffect, useState } from "react";
import { api, PLATFORM_LABELS, WEEKDAYS, formatDateTime } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  CalendarClock, Plus, Trash2, Play, Clock, Repeat, CalendarDays, Pencil,
} from "lucide-react";

const PLATFORMS = ["instagram", "facebook", "x", "linkedin", "tiktok", "youtube", "reddit", "google"];

const emptyForm = {
  name: "",
  enabled: true,
  mode: "weekly",
  days_of_week: [1, 4],
  interval_days: 7,
  time: "08:00",
  keywords: [],
  platforms: ["instagram", "facebook", "x", "linkedin", "tiktok", "youtube", "reddit", "google"],
};

function ScheduleForm({ initial, savedKeywords, onSave }) {
  const [form, setForm] = useState(initial || emptyForm);
  const [kwInput, setKwInput] = useState("");

  const toggleDay = (d) =>
    setForm((f) => ({
      ...f,
      days_of_week: f.days_of_week.includes(d)
        ? f.days_of_week.filter((x) => x !== d)
        : [...f.days_of_week, d].sort(),
    }));

  const togglePlatform = (p) =>
    setForm((f) => ({
      ...f,
      platforms: f.platforms.includes(p) ? f.platforms.filter((x) => x !== p) : [...f.platforms, p],
    }));

  const toggleKeyword = (k) =>
    setForm((f) => ({
      ...f,
      keywords: f.keywords.includes(k) ? f.keywords.filter((x) => x !== k) : [...f.keywords, k],
    }));

  const addKw = () => {
    const t = kwInput.trim();
    if (t && !form.keywords.includes(t)) {
      setForm((f) => ({ ...f, keywords: [...f.keywords, t] }));
      setKwInput("");
    }
  };

  const submit = () => {
    if (!form.name.trim()) return toast.error("Bitte einen Namen für den Zeitplan angeben.");
    if (form.keywords.length === 0) return toast.error("Bitte mindestens einen Suchbegriff wählen.");
    if (form.platforms.length === 0) return toast.error("Bitte mindestens eine Plattform wählen.");
    if (form.mode === "weekly" && form.days_of_week.length === 0)
      return toast.error("Bitte mindestens einen Wochentag wählen.");
    onSave(form);
  };

  const suggestions = Array.from(new Set([...savedKeywords, ...form.keywords]));
  const labelCls = "mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-[#8B8A8E]";

  return (
    <div className="space-y-5">
      <div>
        <label className={labelCls}>Name</label>
        <Input
          data-testid="schedule-name-input"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="z. B. Wöchentliche Marken-Suche"
          className="rounded-md border-[#E0DCD6] bg-[#FAF7F2] focus-visible:ring-[#0A0A0A]"
        />
      </div>

      <div>
        <label className={labelCls}>Wiederholung</label>
        <div className="grid grid-cols-2 gap-2">
          <button
            data-testid="mode-weekly"
            onClick={() => setForm({ ...form, mode: "weekly" })}
            className={`flex items-center justify-center gap-2 rounded-md border px-3 py-2.5 text-sm font-semibold transition-all ${
              form.mode === "weekly" ? "border-[#0A0A0A] bg-[#FAF7F2] text-[#0A0A0A] ring-1 ring-[#0A0A0A]" : "border-[#E8E4DF] text-[#8B8A8E] hover:bg-[#FAF7F2]"
            }`}
          >
            <CalendarDays className="h-4 w-4" /> Wochentage
          </button>
          <button
            data-testid="mode-interval"
            onClick={() => setForm({ ...form, mode: "interval" })}
            className={`flex items-center justify-center gap-2 rounded-md border px-3 py-2.5 text-sm font-semibold transition-all ${
              form.mode === "interval" ? "border-[#0A0A0A] bg-[#FAF7F2] text-[#0A0A0A] ring-1 ring-[#0A0A0A]" : "border-[#E8E4DF] text-[#8B8A8E] hover:bg-[#FAF7F2]"
            }`}
          >
            <Repeat className="h-4 w-4" /> Intervall
          </button>
        </div>
      </div>

      {form.mode === "weekly" ? (
        <div>
          <label className={labelCls}>Wochentage</label>
          <div className="flex flex-wrap gap-2">
            {WEEKDAYS.map((d) => (
              <button
                key={d.value}
                data-testid={`day-${d.value}`}
                onClick={() => toggleDay(d.value)}
                className={`h-10 w-10 rounded-md text-sm font-bold transition-all ${
                  form.days_of_week.includes(d.value)
                    ? "bg-[#0A0A0A] text-white"
                    : "bg-[#F4F1EC] text-[#8B8A8E] hover:bg-[#E8E4DF]"
                }`}
              >
                {d.short}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div>
          <label className={labelCls}>Alle … Tage</label>
          <Input
            data-testid="interval-days-input"
            type="number"
            min={1}
            value={form.interval_days}
            onChange={(e) => setForm({ ...form, interval_days: Math.max(1, parseInt(e.target.value || "1", 10)) })}
            className="w-32 rounded-md border-[#E0DCD6] bg-[#FAF7F2] focus-visible:ring-[#0A0A0A]"
          />
        </div>
      )}

      <div>
        <label className={labelCls}>Uhrzeit (Europe/Berlin)</label>
        <Input
          data-testid="schedule-time-input"
          type="time"
          value={form.time}
          onChange={(e) => setForm({ ...form, time: e.target.value })}
          className="w-40 rounded-md border-[#E0DCD6] bg-[#FAF7F2] focus-visible:ring-[#0A0A0A]"
        />
      </div>

      <div>
        <label className={labelCls}>Geschützte Namen</label>
        <div className="flex gap-2">
          <Input
            data-testid="schedule-keyword-input"
            value={kwInput}
            onChange={(e) => setKwInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addKw())}
            placeholder="Begriff hinzufügen …"
            className="rounded-md border-[#E0DCD6] bg-[#FAF7F2] focus-visible:ring-[#0A0A0A]"
          />
          <Button onClick={addKw} className="rounded-md bg-[#0A0A0A] px-4 text-white hover:bg-[#262626]">
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          {suggestions.length === 0 && <span className="text-sm text-[#8B8A8E]">Noch keine Begriffe.</span>}
          {suggestions.map((k) => (
            <button
              key={k}
              data-testid={`schedule-kw-${k}`}
              onClick={() => toggleKeyword(k)}
              className={`rounded-sm px-3 py-1.5 text-sm font-medium transition-all ${
                form.keywords.includes(k)
                  ? "bg-[#0A0A0A] text-white"
                  : "bg-[#F4F1EC] text-[#8B8A8E] hover:bg-[#E8E4DF]"
              }`}
            >
              {k}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className={labelCls}>Plattformen</label>
        <div className="flex flex-wrap gap-2">
          {PLATFORMS.map((p) => (
            <button
              key={p}
              data-testid={`schedule-platform-${p}`}
              onClick={() => togglePlatform(p)}
              className={`rounded-md px-4 py-2 text-sm font-semibold transition-all ${
                form.platforms.includes(p)
                  ? "bg-[#FAF7F2] text-[#0A0A0A] ring-1 ring-[#0A0A0A]"
                  : "bg-[#F4F1EC] text-[#8B8A8E] hover:bg-[#E8E4DF]"
              }`}
            >
              {PLATFORM_LABELS[p]}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between rounded-md bg-[#F4F1EC] px-4 py-3">
        <span className="text-sm font-semibold text-[#0A0A0A]">Zeitplan aktiv</span>
        <Switch data-testid="schedule-enabled-switch" checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
      </div>

      <Button
        data-testid="save-schedule-button"
        onClick={submit}
        className="w-full rounded-md bg-[#0A0A0A] py-6 text-base font-semibold tracking-wide text-white shadow-sm hover:bg-[#262626]"
      >
        Zeitplan speichern
      </Button>
    </div>
  );
}

function describeSchedule(s) {
  if (s.mode === "weekly") {
    const days = (s.days_of_week || []).map((d) => WEEKDAYS.find((w) => w.value === d)?.short).join(", ");
    return `Jeden ${days || "–"} um ${s.time} Uhr`;
  }
  return `Alle ${s.interval_days} Tage um ${s.time} Uhr`;
}

export default function Schedules() {
  const [schedules, setSchedules] = useState([]);
  const [savedKeywords, setSavedKeywords] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [runs, setRuns] = useState([]);

  const load = async () => {
    const [s, k, r] = await Promise.all([
      api.get("/schedules"),
      api.get("/keywords"),
      api.get("/scan-runs"),
    ]);
    setSchedules(s.data);
    setSavedKeywords(k.data.map((x) => x.term));
    setRuns(r.data.slice(0, 8));
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (form) => {
    try {
      if (editing) {
        await api.put(`/schedules/${editing.id}`, form);
        toast.success("Zeitplan aktualisiert.");
      } else {
        await api.post("/schedules", form);
        toast.success("Zeitplan erstellt.");
      }
      setOpen(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen.");
    }
  };

  const toggleEnabled = async (s) => {
    const { id, created_at, last_run, last_created, next_run, ...rest } = s;
    await api.put(`/schedules/${s.id}`, { ...rest, enabled: !s.enabled });
    load();
  };

  const remove = async (id) => {
    await api.delete(`/schedules/${id}`);
    toast.success("Zeitplan gelöscht.");
    load();
  };

  const runNow = async (s) => {
    toast.info(`Scan für „${s.name}" gestartet …`);
    try {
      const r = await api.post(`/schedules/${s.id}/run`);
      toast.success(`Scan fertig: ${r.data.created} neue Treffer.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scan fehlgeschlagen.");
    }
  };

  return (
    <div className="space-y-6" data-testid="schedules-page">
      <PageHeader title="Zeitplan" subtitle="Automatische Scans an festgelegten Tagen – ganz ohne manuelles Zutun">
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button
              data-testid="new-schedule-button"
              onClick={() => setEditing(null)}
              className="rounded-md bg-[#0A0A0A] text-white shadow-sm hover:bg-[#262626]"
            >
              <Plus className="mr-2 h-4 w-4" /> Neuer Zeitplan
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto rounded-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl font-bold">
                {editing ? "Zeitplan bearbeiten" : "Neuer Zeitplan"}
              </DialogTitle>
            </DialogHeader>
            <ScheduleForm initial={editing} savedKeywords={savedKeywords} onSave={save} />
          </DialogContent>
        </Dialog>
      </PageHeader>

      {schedules.length === 0 ? (
        <Card className="rounded-lg border-[#E8E4DF] bg-white p-12 text-center shadow-[0_2px_16px_rgb(10,10,10,0.03)]">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-md bg-[#0A0A0A]">
            <CalendarClock className="h-7 w-7 text-white" />
          </div>
          <p className="mt-4 font-heading text-lg font-bold text-[#0A0A0A]">Noch kein Zeitplan</p>
          <p className="mt-1 text-sm text-[#6B6A6E]">
            Lege fest, an welchen Tagen automatisch nach euren Marken gesucht wird.
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {schedules.map((s) => (
            <Card
              key={s.id}
              data-testid={`schedule-card-${s.id}`}
              className="rounded-lg border-[#E8E4DF] bg-white p-6 shadow-[0_2px_16px_rgb(10,10,10,0.03)] transition-all hover:shadow-[0_12px_30px_rgb(10,10,10,0.06)]"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={`flex h-11 w-11 items-center justify-center rounded-md ${s.enabled ? "bg-[#0A0A0A]" : "bg-[#E8E4DF]"}`}>
                    <CalendarClock className={`h-5 w-5 ${s.enabled ? "text-white" : "text-[#A6A4A9]"}`} />
                  </div>
                  <div>
                    <p className="font-heading text-lg font-bold text-[#0A0A0A]">{s.name}</p>
                    <p className="text-sm text-[#6B6A6E]">{describeSchedule(s)}</p>
                  </div>
                </div>
                <Switch
                  data-testid={`toggle-schedule-${s.id}`}
                  checked={s.enabled}
                  onCheckedChange={() => toggleEnabled(s)}
                />
              </div>

              <div className="mt-4 flex flex-wrap gap-1.5">
                {s.keywords.map((k) => (
                  <span key={k} className="rounded-sm bg-[#F4F1EC] px-2 py-0.5 text-xs font-medium text-[#3A3A3C]">{k}</span>
                ))}
                {s.platforms.map((p) => (
                  <span key={p} className="rounded-sm border border-[#E8E4DF] bg-white px-2 py-0.5 text-xs font-medium text-[#0A0A0A]">{PLATFORM_LABELS[p]}</span>
                ))}
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3 rounded-md bg-[#F4F1EC] p-3 text-xs">
                <div>
                  <p className="font-semibold uppercase tracking-wide text-[#8B8A8E]">Nächster Lauf</p>
                  <p className="mt-0.5 font-medium text-[#0A0A0A]">{s.enabled ? formatDateTime(s.next_run) : "Pausiert"}</p>
                </div>
                <div>
                  <p className="font-semibold uppercase tracking-wide text-[#8B8A8E]">Letzter Lauf</p>
                  <p className="mt-0.5 font-medium text-[#0A0A0A]">{formatDateTime(s.last_run)}</p>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <Button
                  data-testid={`run-now-${s.id}`}
                  onClick={() => runNow(s)}
                  size="sm"
                  className="rounded-md bg-[#0A0A0A] text-white hover:bg-[#262626]"
                >
                  <Play className="mr-1.5 h-3.5 w-3.5" /> Jetzt ausführen
                </Button>
                <Button
                  data-testid={`edit-schedule-${s.id}`}
                  onClick={() => { setEditing(s); setOpen(true); }}
                  size="sm"
                  variant="outline"
                  className="rounded-md border-[#E0DCD6]"
                >
                  <Pencil className="mr-1.5 h-3.5 w-3.5" /> Bearbeiten
                </Button>
                <Button
                  data-testid={`delete-schedule-${s.id}`}
                  onClick={() => remove(s.id)}
                  size="icon"
                  variant="ghost"
                  className="ml-auto h-9 w-9 rounded-md text-[#A6A4A9] hover:bg-rose-50 hover:text-rose-600"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Card className="overflow-hidden rounded-lg border-[#E8E4DF] bg-white shadow-[0_2px_16px_rgb(10,10,10,0.03)]">
        <div className="flex items-center gap-2 border-b border-[#EDE9E3] px-6 py-5">
          <Clock className="h-4 w-4 text-[#A6A4A9]" />
          <h3 className="font-heading text-lg font-bold text-[#0A0A0A]">Letzte Scans</h3>
        </div>
        {runs.length === 0 ? (
          <p className="px-6 py-8 text-center text-sm text-[#8B8A8E]">Noch keine Scans ausgeführt.</p>
        ) : (
          <div className="divide-y divide-[#EDE9E3]">
            {runs.map((r) => (
              <div key={r.id} className="flex items-center justify-between px-6 py-3.5">
                <div className="flex items-center gap-3">
                  <span className={`rounded-sm px-2.5 py-0.5 text-xs font-semibold ${r.trigger === "automatisch" ? "bg-[#0A0A0A] text-white" : "bg-[#F4F1EC] text-[#3A3A3C]"}`}>
                    {r.trigger}
                  </span>
                  <span className="text-sm text-[#3A3A3C]">{r.schedule_name || (r.keywords || []).join(", ")}</span>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="font-semibold text-emerald-600">+{r.created} neu</span>
                  <span className="text-[#8B8A8E]">{formatDateTime(r.timestamp)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
